/**
 * Chunk-load failure detection, and a hard bound on the automatic reload we
 * allow in response to one.
 *
 * #308: a failed JS chunk fetch during a client-side navigation to /login
 * escaped every React boundary and rendered the bare Next.js error page, and
 * nothing anywhere in apps/web recovered from a failed chunk. This module owns
 * the recovery *decision* so the error boundaries stay presentational and the
 * rule lives in exactly one place.
 *
 * Why the bound is a monotonic counter and not a time window:
 * the obvious recovery - reload whenever a ChunkLoadError is caught - turns a
 * permanently missing chunk (real deploy skew, a genuinely 404ing asset) into
 * an infinite reload loop, which is strictly worse than the dead end it
 * replaces. An "at most one reload per N seconds" guard does not close that
 * hole either: a chunk request that hangs until webpack's load timeout takes
 * longer than any sane window, so every reload would find the window expired
 * and the page would loop forever, just slowly. A counter in sessionStorage is
 * bounded by construction - at most MAX_AUTO_RELOADS per path per tab, and the
 * counter only ever moves in one direction within a tab's lifetime. The
 * terminal state is a branded page with a manual retry, so the user paces any
 * attempt beyond the first.
 */

// webpack sets `error.name = 'ChunkLoadError'`, but the name is lost whenever
// the error crosses a serialization boundary (Next's server digest path, any
// rewrapping reporter), so match the message too. The last three cover native
// ESM dynamic-import failures, which Chrome, Firefox and Safari each word
// differently.
const CHUNK_ERROR_MESSAGE =
  /loading chunk \S+ failed|loading css chunk|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i

export function isChunkLoadError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const { name, message } = error as { name?: unknown; message?: unknown }
  if (name === 'ChunkLoadError') return true
  return typeof message === 'string' && CHUNK_ERROR_MESSAGE.test(message)
}

/**
 * The visitor-facing wording for a chunk failure, shared by every boundary
 * that renders one. Two boundaries show this copy in two different layouts
 * (full page at the root, inside the app chrome in (app)); keeping the words
 * in one place is what stops them drifting apart. The tests assert the
 * literal strings rather than importing this, so a copy change still has to
 * be made deliberately.
 */
export const CHUNK_ERROR_COPY = {
  title: 'This page didn’t finish loading',
  description:
    'Part of the page couldn’t be downloaded. This is usually temporary. Reload to try again.',
  retryLabel: 'Reload the page',
} as const

const RELOAD_CLAIM_PREFIX = 'baohua:chunk-reload:'

// One. A second automatic attempt would land a second or two after the first,
// far too soon for anything that fixes itself on a slower timescale (the cold
// edge-cache fill in #308 resolved about 10s later), so it buys no extra
// recovery while doubling the blast radius of a mistake here. Past the first
// attempt the user paces it by tapping a button.
const MAX_AUTO_RELOADS = 1

/**
 * Claim the automatic reload for `pathname`, returning whether the caller may
 * proceed. Keyed per path so a transient failure on /login cannot spend
 * /signup's attempt, and so the same path can never reload twice in one tab.
 *
 * Fails CLOSED: if sessionStorage is unreadable or unwritable (blocked
 * storage, quota, an embedded webview) we refuse the reload rather than
 * perform one we cannot count. An unbounded loop is a far worse outcome for
 * the visitor than a branded error page.
 *
 * sessionStorage, not localStorage: the claim must survive the reload it
 * authorises (it does - sessionStorage is per tab, not per document) but must
 * not follow the user forever. A fresh tab is a reasonable "they came back to
 * try again" signal and earns a fresh attempt.
 */
export function claimChunkReload(pathname: string): boolean {
  try {
    const storage = window.sessionStorage
    const key = RELOAD_CLAIM_PREFIX + pathname
    const stored = Number.parseInt(storage.getItem(key) ?? '', 10)
    const used = Number.isInteger(stored) && stored > 0 ? stored : 0
    if (used >= MAX_AUTO_RELOADS) return false
    // Record BEFORE the caller navigates. If this write throws we fall to the
    // catch and refuse, so a reload is never performed uncounted.
    storage.setItem(key, String(used + 1))
    return true
  } catch {
    return false
  }
}
